import LoyaltyChooseDoubleRewardsOfferPill from 'takeovers/loyaltyChooseDoubleRewardsTakeover/components/LoyaltyChooseDoubleRewardsOfferPill'
import Group from 'components/utils/Group'
import { useMemo } from 'react'
import type { PackageInclusionsDisclosure } from 'components/AccommodationKit/AccommodationPackageSelection/packageInclusions'
import type { AccommodationRoomQuantityOption } from 'components/AccommodationKit/AccommodationPackageSelection/AccommodationRoomQuantityOptions'
import PackageSelectionQuantityBurnPrice from 'components/OfferPage/packageSelection/PackageSelectionQuantityBurnPrice'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import FlashHotelRateOption from '../SameRoom/FlashHotelRateOption'
import useFlashPackageCardSelection, { type FlashPackageCardSelectionOptions } from './useFlashPackageCardSelection'
import useFlashPackageSelectionCapabilities from './useFlashPackageSelectionCapabilities'

interface Props extends FlashPackageCardSelectionOptions {
  checkIn?: string
  showAllInclusions: boolean
  inclusionsDisclosure: PackageInclusionsDisclosure
  suggestedPackageMessage?: string
}

function FlashPackageCard({ showAllInclusions, inclusionsDisclosure, suggestedPackageMessage, ...selectionOptions }: Props) {
  const { offer, checkIn, view, rooms } = selectionOptions
  const { canAddToCart, canBuyNowBookLater } = useFlashPackageSelectionCapabilities(view)
  const { quantity, quantityFailure, onQuantityChange, roomSelected, selectionPricing, quoteRooms, burnResult, calculationRequests, quantityQuotes, quantityQuoteKey, purchaseDisabled } = useFlashPackageCardSelection(selectionOptions)
  const earnings = useMemo(() => <Group direction="vertical" gap={8} horizontalAlign="start" noDisplayWhenEmpty>
    <LoyaltyChooseDoubleRewardsOfferPill offer={offer} />
    <LoyaltyPointsEarnWithBreakdownModal
      hideTierIcon
      calculationRequests={calculationRequests}
      calculationType={checkIn ? 'exact' : 'estimate'}
      itemTitle={view.package.roomType.name}
      pointsToApply={burnResult.pointsToBurn}
      burnPriceInPoints={burnResult.priceInPoints}
    />
  </Group>, [burnResult.pointsToBurn, burnResult.priceInPoints, calculationRequests, checkIn, offer, view.package.roomType.name])

  const quantityOptions = useMemo((): Array<AccommodationRoomQuantityOption> => quantityQuotes.map(({ quantity, price, quote }) => quote ? {
    quantity,
    priceContent: <PackageSelectionQuantityBurnPrice payment={quote.payment} fetching={quote.fetching} memberPrice={quote.memberPrice} price={price} />,
  } : { quantity, price }), [quantityQuotes])
  const presentation = useMemo(() => ({
    kind: 'option-d' as const,
    showOptions: canAddToCart || canBuyNowBookLater,
    showBookLaterLinks: canBuyNowBookLater,
    earnings,
    burnResult,
    quoteScope: quantity > 0 ? 'selected' as const : 'from' as const,
    selectionPricing,
    occupancyOverride: quoteRooms,
    quantityFailure,
    quantity,
    selectionQuantity: rooms.length,
    roomSelected,
    quantityOptions,
    quantityQuoteKey,
    onQuantityChange,
    purchaseDisabled,
  }), [burnResult, canAddToCart, canBuyNowBookLater, earnings, onQuantityChange, purchaseDisabled, quantity, quantityFailure, quantityOptions, quantityQuoteKey, quoteRooms, roomSelected, rooms.length, selectionPricing])

  return <FlashHotelRateOption
    packageView={view}
    presentation={presentation}
    showAllInclusions={showAllInclusions}
    inclusionsOpen={inclusionsDisclosure.expanded}
    onInclusionsToggle={inclusionsDisclosure.onToggle}
    inclusionsContentId={inclusionsDisclosure.contentId}
    suggestedPackageMessage={suggestedPackageMessage}
  />
}

export default FlashPackageCard
